"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./ContextMenuProvider.module.css";

const ContextMenuContext = createContext(null);

export function ContextMenuProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [items, setItems] = useState([]);
  const containerRef = useRef(null);
  const [submenuItems, setSubmenuItems] = useState([]);
  const [submenuPos, setSubmenuPos] = useState({ x: 0, y: 0 });
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const submenuRef = useRef(null);
  const submenuParentIdRef = useRef(null);
  const isPointerOverSubmenuRef = useRef(false);
  const submenuCloseTimerRef = useRef(null);

  const closeContextMenu = useCallback(() => setIsOpen(false), []);

  const openContextMenu = useCallback((event, nextItems) => {
    event.preventDefault();
    event.stopPropagation();

    const clickX = event.clientX;
    const clickY = event.clientY;
    const { innerWidth, innerHeight } = window;
    const estimatedWidth = 200;
    const estimatedHeight = Math.min(320, 32 * nextItems.length + 8);
    const x = Math.min(clickX, innerWidth - estimatedWidth - 8);
    const y = Math.min(clickY, innerHeight - estimatedHeight - 8);

    setItems(nextItems);
    setPosition({ x, y });
    setIsOpen(true);
    setIsSubmenuOpen(false);
    setSubmenuItems([]);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const onDown = (e) => {
      const clickOutsideMain = !containerRef.current?.contains(e.target);
      const clickOutsideSub = !submenuRef.current || !submenuRef.current.contains(e.target);
      if (clickOutsideMain && clickOutsideSub) {
        setIsOpen(false);
        setIsSubmenuOpen(false);
      }
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    const onResize = () => setIsOpen(false);

    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("contextmenu", onDown, true);
    window.addEventListener("keydown", onEscape, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("contextmenu", onDown, true);
      window.removeEventListener("keydown", onEscape, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isOpen]);

  const value = useMemo(
    () => ({
      isOpen,
      position,
      items,
      openContextMenu,
      closeContextMenu,
    }),
    [isOpen, position, items, openContextMenu, closeContextMenu]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!containerRef.current) return;
    const margin = 8;
    const rect = containerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let nextX = position.x;
    let nextY = position.y;
    if (rect.right > vw - margin) {
      nextX = Math.max(margin, vw - rect.width - margin);
    }
    if (rect.bottom > vh - margin) {
      nextY = Math.max(margin, vh - rect.height - margin);
    }
    if (nextX !== position.x || nextY !== position.y) {
      setPosition({ x: nextX, y: nextY });
    }
  }, [isOpen, items, position.x, position.y]);

  useEffect(() => {
    if (!isSubmenuOpen) return;
    if (!submenuRef.current) return;
    const margin = 8;
    const rect = submenuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let nextX = submenuPos.x;
    let nextY = submenuPos.y;
    if (rect.right > vw - margin) {
      nextX = Math.max(margin, vw - rect.width - margin);
    }
    if (rect.bottom > vh - margin) {
      nextY = Math.max(margin, vh - rect.height - margin);
    }
    if (nextX !== submenuPos.x || nextY !== submenuPos.y) {
      setSubmenuPos({ x: nextX, y: nextY });
    }
  }, [isSubmenuOpen, submenuItems, submenuPos.x, submenuPos.y]);

  return (
    <ContextMenuContext.Provider value={value}>
      {children}

      {isOpen &&
        createPortal(
          <div className={styles.overlay}>
            <div
              ref={containerRef}
              className={styles.menu}
              style={{ left: position.x, top: position.y }}
              role="menu"
            >
              <ul className={styles.menuList}>
                {items.map((item) => (
                  <li
                    key={item.id}
                    className={styles.menuItem}
                    onMouseEnter={(e) => {
                      if (Array.isArray(item.children) && item.children.length > 0) {
                        if (submenuCloseTimerRef.current) {
                          clearTimeout(submenuCloseTimerRef.current);
                          submenuCloseTimerRef.current = null;
                        }
                        const rect = e.currentTarget.getBoundingClientRect();
                        const estHeight = Math.min(320, 32 * item.children.length + 8);
                        let x = Math.min(rect.right - 8, window.innerWidth - 180);
                        if (item.submenuDirection === "left") {
                          x = Math.max(8, rect.left - 185);
                        }
                        const y = Math.min(rect.top, window.innerHeight - estHeight - 8);
                        setSubmenuItems(item.children);
                        setSubmenuPos({ x, y });
                        setIsSubmenuOpen(true);
                        submenuParentIdRef.current = item.id;
                      } else {
                        setIsSubmenuOpen(false);
                        setSubmenuItems([]);
                        submenuParentIdRef.current = null;
                      }
                    }}
                    onMouseLeave={() => {
                      if (submenuParentIdRef.current === item.id) {
                        if (submenuCloseTimerRef.current) {
                          clearTimeout(submenuCloseTimerRef.current);
                        }
                        submenuCloseTimerRef.current = setTimeout(() => {
                          submenuCloseTimerRef.current = null;
                          if (!isPointerOverSubmenuRef.current) {
                            setIsSubmenuOpen(false);
                            setSubmenuItems([]);
                            submenuParentIdRef.current = null;
                          }
                        }, 80);
                      }
                    }}
                  >
                    <button
                      type="button"
                      className={`${styles.menuButton} ${item.danger ? styles.menuButtonDanger : ""}`}
                      onClick={() => {
                        if (Array.isArray(item.children) && item.children.length > 0) return;
                        closeContextMenu();
                        item.onSelect?.();
                      }}
                      role="menuitem"
                    >
                      <span className={styles.label}>{item.label}</span>
                      {Array.isArray(item.children) && item.children.length > 0 && (
                        <span className={styles.arrow}></span>
                      )}
                      {item.kbd && <span className={styles.kbd}>{item.kbd}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {isSubmenuOpen && submenuItems.length > 0 && (
              <div
                ref={submenuRef}
                className={styles.submenu}
                style={{ left: submenuPos.x, top: submenuPos.y }}
                role="menu"
                onMouseEnter={() => {
                  isPointerOverSubmenuRef.current = true;
                }}
                onMouseLeave={() => {
                  isPointerOverSubmenuRef.current = false;
                  setIsSubmenuOpen(false);
                  setSubmenuItems([]);
                  submenuParentIdRef.current = null;
                }}
              >
                <ul className={styles.menuList}>
                  {submenuItems.map((child, index) => {
                    const key = child.id || child.label || `submenu-item-${index}`;
                    return (
                      <React.Fragment key={key}>
                        <li className={styles.menuItem}>
                          <button
                            type="button"
                            className={styles.menuButton}
                            onClick={() => {
                              setIsOpen(false);
                              setIsSubmenuOpen(false);
                              child.onSelect?.();
                            }}
                            role="menuitem"
                          >
                            <span className={styles.label}>{child.label}</span>
                            {child.kbd && <span className={styles.kbd}>{child.kbd}</span>}
                          </button>
                        </li>
                        {index === 0 && !child.hideDivider && (
                          <li
                            className={styles.submenuDivider}
                            role="separator"
                            aria-hidden="true"
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>,
          document.body
        )}
    </ContextMenuContext.Provider>
  );
}

export const useContextMenu = () => {
  const ctx = useContext(ContextMenuContext);
  if (!ctx) throw new Error("useContextMenu must be used within ContextMenuProvider");
  return ctx;
};
